import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Descriptions, Skeleton, Table, Typography, message } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api-client';
import { formatMoney, isPositive } from '../lib/money';
import { ActionDrawer } from './ActionDrawer';
import { billSale, previewSale, type SalePreview } from './bill-sale-api';

const { Text } = Typography;

type PreviewLine = SalePreview['lines'][number] & { key: string };

/**
 * Bill this sale: shows the invoice that will be made — every line generated
 * from the booking's quotation and financing, nothing typed — before it is
 * made (CLAUDE.md §10). A refusal (registration, financing, loan) comes from
 * the preview, so it is read here rather than after pressing Bill.
 *
 * Shared by the booking page and the Till; each passes `onBilled` to refresh
 * the queries its own feature keys on.
 */
export function BillSaleDrawer({
  bookingUid,
  open,
  onClose,
  onBilled,
}: {
  bookingUid: string;
  open: boolean;
  onClose: () => void;
  onBilled: () => void;
}): ReactNode {
  const { t } = useTranslation();

  const preview = useQuery({
    queryKey: ['sale-preview', bookingUid],
    queryFn: () => previewSale(bookingUid),
    enabled: open,
    // A preview is only true for the moment it is read: always re-read on open.
    staleTime: 0,
    gcTime: 0,
  });

  const bill = useMutation({
    mutationFn: () => billSale(bookingUid),
    onSuccess: (result) => {
      onBilled();
      onClose();
      void message.success(t('bookings.billed', { invoiceNo: result.invoiceNo }));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('bookings.billFailed'));
    },
  });

  const data = preview.data;
  const previewError =
    preview.error === null ? undefined : (
      <Alert
        type="error"
        showIcon
        message={preview.error instanceof ApiError ? preview.error.message : t('bookings.previewFailed')}
      />
    );

  return (
    <ActionDrawer
      open={open}
      title={t('bookings.bill')}
      submitLabel={t('bookings.bill')}
      submitting={bill.isPending}
      submitDisabled={data === undefined || preview.error !== null}
      onClose={onClose}
      onSubmit={() => {
        bill.mutate();
      }}
      error={previewError}
      consequence={
        data === undefined
          ? undefined
          : t('bookings.billConsequence', {
              total: formatMoney(data.grandTotal),
              applied: formatMoney(data.advancesApplied),
              balance: formatMoney(data.balanceDue),
            })
      }
    >
      {preview.isLoading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : data === undefined ? null : (
        <>
          <Table<PreviewLine>
            // Preview lines carry no uid yet; their order is the invoice's order.
            dataSource={data.lines.map((line, i) => ({ ...line, key: String(i) }))}
            pagination={false}
            size="small"
            columns={[
              { title: t('till.fields.item'), dataIndex: 'description' },
              { title: t('bookings.fields.qty'), dataIndex: 'qty', align: 'right' as const },
              {
                title: t('till.fields.total'),
                dataIndex: 'lineTotal',
                align: 'right' as const,
                render: (value: string) => <span className="tabular">{formatMoney(value)}</span>,
              },
            ]}
          />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={t('bookings.billFields.subtotal')}>
              {formatMoney(data.subtotal)}
            </Descriptions.Item>
            <Descriptions.Item label={t('bookings.billFields.discount')}>
              {formatMoney(data.discountTotal)}
            </Descriptions.Item>
            <Descriptions.Item label={t('bookings.billFields.tax')}>{formatMoney(data.taxTotal)}</Descriptions.Item>
            <Descriptions.Item label={t('till.fields.total')}>
              <Text strong>{formatMoney(data.grandTotal)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('bookings.billFields.applied')}>
              {formatMoney(data.advancesApplied)}
            </Descriptions.Item>
            <Descriptions.Item label={t('bookings.billFields.balance')}>
              <Text strong>{formatMoney(data.balanceDue)}</Text>
            </Descriptions.Item>
            {isPositive(data.creditLeft) && (
              <Descriptions.Item label={t('bookings.billFields.creditLeft')}>
                {formatMoney(data.creditLeft)}
              </Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}
    </ActionDrawer>
  );
}
